import _ from "lodash";
import React from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";
import classes from "./SubjectGroupSelect.scss";
import classNames from "classnames";
import I18nHOC from "UIComponents/I18nHOC";
import update from "immutability-helper";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { MYP_INTERDISCIPLINARY } from "Constants/stringConstants";
import {
  Alert,
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Modal,
  Tag,
  ChecklistDropdown,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  DeleteOutlined,
  GoalsOutlined,
  OpenBookOutlined,
  OpenBookTextOutlined,
  WarningCircleFilled,
} from "@toddle-edu/ds-icons";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

export class SubjectGroupSelect extends UIBaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      showEditModal: false,
      showObjectiveRemoveModal: false,
      objectiveUpdateValue: {},
      removedObjectiveLevel: {},
    };
    this.pairRefs = [];
    this.labelContainerRefs = new Map(); // Store refs for each subject label container

    // Store callback functions to prevent recreating them on each render
    this.subjectTitleLabelContainerRefCallbacks = {};
    this.objectiveTitleLabelContainerRefCallbacks = {};
  }
  getFormattedObjectiveLevelOptions = objectiveLevelOptions => {
    return _.map(objectiveLevelOptions, option => ({
      value: option.value,
      label: option.label,
      disabled: option.isDisabled,
    }));
  };

  componentDidMount = () => {
    this.createSubjectGroupPairFromValue();
  };

  componentDidUpdate = prevProps => {
    const { value, multi } = this.props;

    if (
      (!_.isEqual(_.sortBy(prevProps.value || []), _.sortBy(value || [])) ||
        prevProps.multi !== multi) &&
      !(_.isEmpty(prevProps.value) && _.isEmpty(value))
    ) {
      this.createSubjectGroupPairFromValue();
    }
  };

  componentWillUnmount() {
    this.unregisterAllLabels();
  }

  // Register all current label containers
  registerAllLabels = () => {
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;
    const { subjectGroupPair } = this.state;

    if (!shouldRegisterLabelContainerRef) return;
    // Prevent infinite loop by storing previous registered labels
    const registeredLabels = new Set();

    _.forEach(subjectGroupPair, (pair, index) => {
      // Register subject title label container
      const subjectTitleLabelContainerRef = this.labelContainerRefs.get(
        `subject-container-${index}`
      );
      const subjectKey = `${fieldKey}-subject-container-${index}`;

      if (subjectTitleLabelContainerRef && !registeredLabels.has(subjectKey)) {
        registeredLabels.add(subjectKey);
        registerLabelContainerRef(subjectKey, {
          current: subjectTitleLabelContainerRef,
        });
      }

      // Register objective title label container
      const objectiveTitleLabelContainerRef = this.labelContainerRefs.get(
        `objective-container-${index}`
      );
      const objectiveKey = `${fieldKey}-objective-container-${index}`;

      if (
        objectiveTitleLabelContainerRef &&
        !registeredLabels.has(objectiveKey)
      ) {
        registeredLabels.add(objectiveKey);
        registerLabelContainerRef(objectiveKey, {
          current: objectiveTitleLabelContainerRef,
        });
      }
    });
  };

  // Unregister all label containers
  unregisterAllLabels = () => {
    const {
      registerLabelContainerRef,
      fieldKey,
      shouldRegisterLabelContainerRef,
    } = this.props;
    const { subjectGroupPair } = this.state;

    if (!shouldRegisterLabelContainerRef) return;
    _.forEach(subjectGroupPair, (pair, index) => {
      registerLabelContainerRef(`${fieldKey}-subject-container-${index}`, null);
      registerLabelContainerRef(
        `${fieldKey}-objective-container-${index}`,
        null
      );
    });
  };

  // Create ref callback for subject title label containers
  createSubjectTitleLabelContainerRefCallback = index => {
    const { registerLabelContainerRef } = this.props;
    if (!registerLabelContainerRef) return;
    if (!this.subjectTitleLabelContainerRefCallbacks[index]) {
      this.subjectTitleLabelContainerRefCallbacks[index] = ref => {
        // Only register if the ref actually changed
        if (ref !== this.labelContainerRefs.get(`subject-container-${index}`)) {
          this.labelContainerRefs.set(`subject-container-${index}`, ref);
          const { registerLabelContainerRef, fieldKey } = this.props;

          registerLabelContainerRef(
            `${fieldKey}-subject-container-${index}`,
            ref ? { current: ref } : null
          );
        }
      };
    }

    return this.subjectTitleLabelContainerRefCallbacks[index];
  };

  // Create ref callback for objective title label containers
  createObjectiveTitleLabelContainerRefCallback = index => {
    const { registerLabelContainerRef } = this.props;
    if (!registerLabelContainerRef) return;
    if (!this.objectiveTitleLabelContainerRefCallbacks[index]) {
      this.objectiveTitleLabelContainerRefCallbacks[index] = ref => {
        // Only register if the ref actually changed
        if (
          ref !== this.labelContainerRefs.get(`objective-container-${index}`)
        ) {
          this.labelContainerRefs.set(`objective-container-${index}`, ref);
          const { registerLabelContainerRef, fieldKey } = this.props;

          registerLabelContainerRef(
            `${fieldKey}-objective-container-${index}`,
            ref ? { current: ref } : null
          );
        }
      };
    }

    return this.objectiveTitleLabelContainerRefCallbacks[index];
  };

  //get selected objective id for the given subject
  getSelectedObjectiveId = ({ subjectId }) => {
    const { options } = this.props;
    const objectiveLevelNodes = _.get(options, "objectiveLevelNodes", []);
    let selectedObjectiveLevels = _.get(options, "selectedObjectiveLevels", []);

    //filter out the empty objectives
    selectedObjectiveLevels = _.filter(selectedObjectiveLevels, Boolean);

    //fetch the objective with subject from selected objective levels
    const objectiveLevelId = [];
    _.forEach(selectedObjectiveLevels, objectiveId => {
      const objective = _.find(objectiveLevelNodes, { id: objectiveId });
      if (
        _.find(_.get(objective, "dataSetNodeWrapper.properties.subjects", []), {
          id: subjectId,
        })
      ) {
        objectiveLevelId.push(objectiveId);
      }
    });

    return objectiveLevelId;
  };

  createSubjectGroupPairFromValue = () => {
    const {
      options,
      value,
      multi,
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
    } = this.props;
    const initSubject = _.get(options, "initSubject", {});

    const subjectGroupPair = _.map(value, val => {
      const subjectGroups = _.get(options, "subjectGroups", []);

      const subjectGroup = _.find(subjectGroups, subjectGroup => {
        const subjects = _.get(subjectGroup, "subjects", []);
        return _.findIndex(subjects, subject => subject.id == val) >= 0;
      });
      const subjectGroupId = _.get(subjectGroup, "id", null);
      const selectedObjectiveLevelIds = this.getSelectedObjectiveId({
        subjectId: val,
      });
      const objectiveLevelId =
        _.size(selectedObjectiveLevelIds) > 0 ? selectedObjectiveLevelIds : "";

      return {
        subjectGroup: subjectGroupId,
        subject: val,
        ...(isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled
          ? { objectiveLevelId }
          : {}),
      };
    });

    if (
      (subjectGroupPair.length === 1 && multi) ||
      subjectGroupPair.length === 0
    ) {
      subjectGroupPair.push({
        subjectGroup: "",
        subject: "",
        ...(isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled
          ? { objectiveLevelId: "" }
          : {}),
      });
    }

    const subjectGroupPairList = this.getSubjectList({
      initSubject,
      subjectGroupPair,
    });

    this.setState({ subjectGroupPair: subjectGroupPairList }, () => {
      // This ensures it only runs after the state has actually changed
      setTimeout(() => {
        this.registerAllLabels();
      }, 0);
    });
  };

  // genrates final pair list in which 1st subject will be initSubject
  getSubjectList = ({ initSubject, subjectGroupPair }) => {
    const initSubjectId = _.get(initSubject, "id", null);
    const firstSubject = _.find(
      subjectGroupPair,
      { subject: initSubjectId },
      null
    );
    const restSubject = _.filter(
      subjectGroupPair,
      pair => pair.subject !== initSubjectId
    );
    if (firstSubject) {
      return [firstSubject, ...restSubject];
    } else {
      //this is added to handle the import of unit from the course where current course subject is not selected
      return [...restSubject];
    }
  };

  onAddClick = () => {
    const {
      subjectGroupPair,
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
    } = this.state;
    this.setState({
      subjectGroupPair: [
        ...subjectGroupPair,
        {
          subjectGroup: "",
          subject: "",
          ...(isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled
            ? { objectiveLevelId: "" }
            : {}),
        },
      ],
    });
    this.pairRefs = update(this.pairRefs, {
      $push: [React.createRef()],
    });
  };

  onDeleteClick = ({ index }) => {
    const { subjectGroupPair } = this.state;
    this.pairRefs = update(this.pairRefs, { $unset: [index] });
    this.setState({
      subjectGroupPair: update(subjectGroupPair, { $splice: [[index, 1]] }),
    });

    setTimeout(() => {
      this.updateValueLocally();
    });
  };

  updateObjectiveOption = ({ index, key, value }) => {
    const { parentType, isSubjectEditModal = true } = this.props;
    if (
      ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION) &&
      (parentType == "UNIT_PLAN" || isSubjectEditModal)
    ) {
      const { subjectGroupPair } = this.state;
      const selectedObjective = subjectGroupPair[index].objectiveLevelId || [];
      const currentSelectedObjectives = _.isArray(selectedObjective)
        ? selectedObjective
        : [selectedObjective];
      const removedObjectives = _.difference(currentSelectedObjectives, value);

      const { options } = this.props;
      const objectiveLevelNodes = _.get(options, "objectiveLevelNodes", []);

      if (removedObjectives.length > 0) {
        const removedObjectiveLevel = _.find(objectiveLevelNodes, {
          id: removedObjectives[0],
        });
        this.setState({
          showObjectiveRemoveModal: true,
          objectiveUpdateValue: { index, key, value },
          removedObjectiveLevel,
        });
      } else {
        this.updateOption({
          key: "objectiveLevelId",
          value,
          index,
        });
      }
    } else {
      this.updateOption({
        key: "objectiveLevelId",
        value,
        index,
      });
    }
  };

  updateOption = ({ index, key, value, subjectGroupId }) => {
    const {
      options,
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
    } = this.props;
    const objectiveLevelNodes = _.get(options, "objectiveLevelNodes", []);
    const selectedObjectiveLevelsWithValue = _.get(
      options,
      "selectedObjectiveLevelsWithValue",
      []
    );
    const { subjectGroupPair } = this.state;
    const oldParams = subjectGroupPair[index];
    const newParams = { [key]: value };
    const oldValue = subjectGroupPair[index][key];
    if (key == "subject" && oldValue != value) {
      newParams["subjectGroup"] = subjectGroupId;
      //condition to handle if objective for this particular subject is already selected
      //if objective is already selected for this subject in past and user mistakenly
      //removed that subject and if user add that subject again than objective for it will be pre-selected
      //pre-selecteed objectives are the one which was selected before deleting it
      const objectiveLevels = this.getObjectiveLevelOptions({
        objectiveLevelNodes,
        subject: newParams["subject"],
        SubjectGroupList: options?.subjectGroups,
        courseDetails: options?.courseDetails,
        subjectGroup: subjectGroupId,
      });
      const objectiveLevelOptions = _.map(
        objectiveLevels,
        objectivelevel => objectivelevel.value
      );
      const selectedObjectiveLevelIds = _.intersection(
        selectedObjectiveLevelsWithValue,
        objectiveLevelOptions
      );
      if (isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled) {
        if (_.size(selectedObjectiveLevelIds) == 1) {
          newParams["objectiveLevelId"] = selectedObjectiveLevelIds[0];
        } else if (
          objectiveLevels[0]?.recommended &&
          isObjectivePhaseRecommendationEnabled
        ) {
          newParams["objectiveLevelId"] = objectiveLevels[0].recommended;
        } else {
          //this condition will be handles when subject will be changed and no objectives are selected for it
          newParams["objectiveLevelId"] = "";
        }
      }
    }
    this.setState({
      subjectGroupPair: update(subjectGroupPair, {
        [index]: {
          $set: { ...oldParams, ...newParams },
        },
      }),
    });

    const updateValueForKeys = ["subject"];
    if (isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled) {
      updateValueForKeys.push("objectiveLevelId");
    }
    //Send only when subject or objective field toggle
    if (_.includes(updateValueForKeys, key)) {
      setTimeout(() => {
        this.updateValueLocally();
      });
    }
  };

  //Converting subjectGroupPair into subject and subjectGroups and send them to parent
  updateValueLocally = () => {
    const {
      options,
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
    } = this.props;
    const { subjectGroupPair } = this.state;
    const subjectGroups = _.get(options, "subjectGroups", []);
    const {
      value,
      nodes,
      subjectGroupValue,
      subjectGroupNodes,
      objectiveLevelValue,
    } = _.reduce(
      subjectGroupPair,
      (result, pair) => {
        //subject group will be given empty string for added tabs which are not yet selected
        const subjectGroup =
          _.find(
            subjectGroups,
            sg => _.findIndex(sg.subjects, sub => sub.id == pair.subject) >= 0
          ) || "";
        const subject = _.find(
          subjectGroup?.subjects,
          sub => sub.id == pair.subject
        );
        if (isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled) {
          result.objectiveLevelValue = result.objectiveLevelValue || [];
          if (pair.objectiveLevelId) {
            const objectiveLevelIds = _.isArray(pair.objectiveLevelId)
              ? pair.objectiveLevelId
              : [pair.objectiveLevelId];
            result.objectiveLevelValue.push(...objectiveLevelIds);
          }
        }
        if (pair.subject) result.value.push(pair.subject);
        if (!_.isEmpty(subjectGroup))
          result.nodes.push({ node: { ...subject, subjectGroup } });
        if (
          !_.includes(result.subjectGroupValue, subjectGroup.id) &&
          !_.isEmpty(subjectGroup)
        ) {
          result.subjectGroupValue.push(subjectGroup.id);
          result.subjectGroupNodes.push({ node: subjectGroup });
        }

        return result;
      },
      {
        value: [],
        nodes: [],
        subjectGroupValue: [],
        subjectGroupNodes: [],
        objectiveLevelValue: [],
      }
    );

    this.updateValue(value, {
      nodes,
      subjectGroupValue,
      subjectGroupNodes,
      ...(isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled
        ? {
            objectiveLevelValue,
            objectiveLevelNodes: _.filter(options.objectiveLevelNodes, option =>
              _.includes(objectiveLevelValue, option.id)
            ),
          }
        : {}),
    });
  };

  //get the objectives for the selected subject
  getObjectiveLevelOptions = ({
    objectiveLevelNodes,
    subject,
    SubjectGroupList,
    courseDetails,
    subjectGroup,
    selectedObjectiveLevelsWithValue,
  }) => {
    const { isObjectivePhaseRecommendationEnabled } = this.props;
    const currentSubjectGroup = _.find(SubjectGroupList, { id: subjectGroup });
    const currentSubject = _.find(_.get(currentSubjectGroup, "subjects", []), {
      id: subject,
    });
    const currentGrade = _.get(courseDetails, "primaryGrade.id", "");
    const currentSubjectRecommendedPhase = _.find(
      _.get(currentSubject, "recommendedPhase", []),
      { gradeId: currentGrade }
    );
    let objectiveLevels = [];
    const recommendedObjective = [];
    // iterating through all the objective and find the objectives that are related to selected subject
    _.forEach(objectiveLevelNodes, objectiveLevelNode => {
      if (
        _.find(
          _.get(
            objectiveLevelNode,
            "dataSetNodeWrapper.properties.subjects",
            []
          ),
          { id: subject }
        )
      ) {
        const recommendedObjectiveId = currentSubjectRecommendedPhase?.phaseId;
        const optionData = {
          value: objectiveLevelNode.id,
          isDisabled:
            isObjectivePhaseRecommendationEnabled &&
            _.includes(selectedObjectiveLevelsWithValue, objectiveLevelNode.id),
          label: (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                columnGap: "4px",
              }}
              size="xx-small"
            >
              {objectiveLevelNode.label}{" "}
              {_.isEqual(
                currentSubjectRecommendedPhase?.phaseId,
                objectiveLevelNode?.id
              ) &&
                isObjectivePhaseRecommendationEnabled && (
                  <Tag size="small" color="teal" dsVersion="2.0">
                    {t("unitPlan:recommended")}
                  </Tag>
                )}
            </span>
          ),
          name: objectiveLevelNode.label,
          recommended: currentSubjectRecommendedPhase?.phaseId,
        };

        if (
          currentSubjectRecommendedPhase &&
          recommendedObjectiveId == objectiveLevelNode.id
        ) {
          recommendedObjective.push(optionData);
        } else {
          objectiveLevels.push(optionData);
        }
      }
    });
    objectiveLevels = _.concat(recommendedObjective, objectiveLevels);
    return objectiveLevels;
  };

  getSubjectGroupOptions = () => {
    const { options } = this.props;
    const subjectGroupOptions = _.get(options, "subjectGroups", []);

    return _.map(subjectGroupOptions, opt => {
      return { label: opt.name, value: opt.id };
    });
  };

  getSubjectGroupOptionsLabels = ({ subjectId, subjectGroupId }) => {
    const { options } = this.props;
    const subjectGroupOptions = _.get(options, "subjectGroups", []);
    const subjectGroup = subjectGroupOptions.find(
      group => group.id === subjectGroupId
    );
    const subject = subjectGroup?.subjects.find(sub => sub.id === subjectId);

    return {
      subjectGroup: subjectGroup ? subjectGroup.name : null,
      subject: subject ? subject.name : null,
    };
  };

  getSubjectAndSubjectGroupOptions = () => {
    const { options } = this.props;
    const { subjectGroupPair } = this.state;
    const subjectGroupOptions = _.get(options, "subjectGroups", []);

    // Get ALL selected subjects from subjectGroupPair (including the current one)
    const selectedSubjects = _.reduce(
      subjectGroupPair,
      (result, pair) => {
        if (pair.subject) {
          result.push(pair.subject);
        }
        return result;
      },
      []
    );

    return _.reduce(
      subjectGroupOptions,
      (result, subjectGroupItem) => {
        // Skip ALL subjects that are already selected in ANY row
        const filteredSubjects = _.filter(
          subjectGroupItem.subjects,
          item => !_.includes(selectedSubjects, item.id)
        );

        // Skip subjectGroups that have no subjects left
        if (_.isEmpty(filteredSubjects)) {
          return result;
        }

        // Add the valid subject group to the result
        result.push({
          key: subjectGroupItem.id,
          label: subjectGroupItem.name,
          isItemGroup: true,
          options: filteredSubjects.map(subjectItem => ({
            key: subjectItem.id,
            label: subjectItem.name,
          })),
        });

        return result;
      },
      []
    );
  };

  handleSubjectGroupSelect = (value, index) => {
    const { options } = this.props;
    const subjectGroupOptions = _.get(options, "subjectGroups", []);
    const subjectGroup = _.find(subjectGroupOptions, group =>
      _.find(group.subjects, { id: value.key })
    );

    this.updateOption({
      key: "subject",
      value: value.key,
      subjectGroupId: subjectGroup ? subjectGroup.id : null,
      index,
    });
  };

  //Remove those subjects which are already selected
  getSubjectOptions = ({ subjectGroup, subject }) => {
    const { options } = this.props;
    const { subjectGroupPair } = this.state;
    const subjectGroupOptions = _.get(options, "subjectGroups", []);
    let subjectOptions = _.get(
      _.find(subjectGroupOptions, { id: subjectGroup }),
      "subjects",
      []
    );
    subjectOptions = _.filter(subjectOptions, sg => {
      return !_.find(
        subjectGroupPair,
        pair => pair.subject == sg.id && pair.subject != subject
      );
    });

    return _.map(subjectOptions, opt => {
      return {
        label: opt.name,
        value: opt.id,
        isArchived: _.get(opt, "isArchived", false),
      };
    });
  };

  renderEditSubjectGroupSelect = () => {
    const {
      subjectGroupPair,
      showObjectiveRemoveModal,
      removedObjectiveLevel,
      objectiveUpdateValue,
    } = this.state;
    const {
      t,
      options: {
        initSubject = {},
        objectiveLevelNodes,
        selectedObjectiveLevelsWithValue,
        unitType,
        courseDetails,
        subjectGroups: SubjectGroupList,
      } = {},
      multi,
      value,
      options,
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
      mode,
      isObjectiveLevelsHidden,
      subjectTitleStyles,
      isLearningCourseEnabled,
    } = this.props;

    const isInterDisciplinaryUnitPlan = unitType == MYP_INTERDISCIPLINARY;

    const isViewDisable = mode === "view" ? true : false;

    return (
      <div className={classes.container}>
        <div className={classes.subjectGroupPairs}>
          {_.map(subjectGroupPair, (pair, index) => {
            const { subjectGroup, subject, objectiveLevelId } = pair;

            const objectiveLevelOptions = this.getObjectiveLevelOptions({
              objectiveLevelNodes,
              subject,
              SubjectGroupList,
              courseDetails,
              subjectGroup,
              selectedObjectiveLevelsWithValue,
            });
            //For interdisciplinary unit the 1 subject will always be the subject of course so it will be always disabled

            const selectedValue = this.getSelectValue({
              pair,
              objectiveLevelOptions,
            });
            let objectiveLabels;
            if (!_.isEmpty(objectiveLevelOptions)) {
              if (_.isArray(objectiveLevelId)) {
                if (_.isArray(selectedValue) && selectedValue.length > 0) {
                  objectiveLabels = _.join(_.map(selectedValue, "name"), ", ");
                } else {
                  objectiveLabels = t("unitPlan:select_year_phase");
                }
              } else if (selectedValue) {
                objectiveLabels = _.get(selectedValue, "name", "");
              } else {
                objectiveLabels = t("unitPlan:select_year_phase");
              }
            } else {
              objectiveLabels = t("unitPlan:select_year_phase");
            }

            const isEditDisabled =
              (_.get(initSubject, "id", "") == pair.subject || index == 0) &&
              !_.isEmpty(value);

            const canBeDeleted = subjectGroupPair.length > 2;
            const subjectLabel = classNames({
              [classes.subjectLabel]: true,
              [classes.subjectLabelEditEnabled]:
                !isEditDisabled && canBeDeleted,
              "text-body": true,
            });
            //get the objectives with selected objectives
            //handling old unit where multiple objective levels are selected
            const selectedObjectiveLevelIds = _.intersection(
              selectedObjectiveLevelsWithValue,
              _.map(
                this.getObjectiveLevelOptions({
                  objectiveLevelNodes,
                  subject,
                  SubjectGroupList,
                  courseDetails,
                  subjectGroup,
                }),
                objectivelevel => objectivelevel.value
              )
            );
            /*selectedObjectiveLevelsWithValue consist of array of all elements which are selected in the objectivesMYP section
          eg for interdisciplinary there are objective selected in Disciplinary_subjectname so the value of objectives parent 
          will be stored in this
          eg for subject specific unit there is a objective tab so the value of the selected objective's parent will be stored here
          */
            //also disable the objective levels for the subject with multiple objectives selected(old units)
            const isObjectiveLevelOptionDisabled = ACLStore.can(
              ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION
            )
              ? false
              : _.includes(
                  options.selectedObjectiveLevelsWithValue,
                  _.isArray(objectiveLevelId)
                    ? objectiveLevelId[0]
                    : objectiveLevelId
                ) ||
                isInterDisciplinaryUnitPlan ||
                selectedObjectiveLevelIds.length > 1;

            const objectiveLevelLabel = _.get(
              _.find(objectiveLevelOptions, {
                value: _.isArray(objectiveLevelId)
                  ? objectiveLevelId[0]
                  : objectiveLevelId,
              }),
              "name",
              ""
            );

            const recommendedObjectiveLevel =
              objectiveLevelOptions &&
              _.find(objectiveLevelOptions, {
                value: objectiveLevelOptions[0]?.recommended,
              });
            const subjectAndSubjectGroupLabel = this.getSubjectGroupOptionsLabels(
              { subjectId: subject, subjectGroupId: subjectGroup }
            );
            const titleContainerClass = classNames({
              [classes.titleContainer]: isInterDisciplinaryUnitPlan,
              [classes.SubjectSpecificTitleContainer]: true,
            });
            const recommendedObjectiveLevelIsUnSelected =
              isObjectivePhaseRecommendationEnabled &&
              recommendedObjectiveLevel &&
              !_.includes(objectiveLevelId, recommendedObjectiveLevel.value) &&
              !_.isEmpty(objectiveLevelId) &&
              options.courseDetails;

            const childPairClasses = classNames({
              [classes.childPair]: true,
              [classes.childPairV2]: isLearningCourseEnabled,
            });

            return (
              <div className={classes.pairContainer} key={index}>
                {!isLearningCourseEnabled && (
                  <div className={classes.pair}>
                    <div
                      className={titleContainerClass}
                      style={subjectTitleStyles}
                      ref={this.createSubjectTitleLabelContainerRefCallback(
                        index
                      )}
                    >
                      <OpenBookTextOutlined size="xx-small" />
                      <div className={classNames("text-label-l")}>
                        {isInterDisciplinaryUnitPlan
                          ? t("unitPlan:unitType_subject_count", {
                              unitType: t("common:interdisciplinary"),
                              count: index + 1,
                            })
                          : t("common:subject")}
                      </div>
                    </div>
                    <div className={classes.dropdownWithRemoveContainer}>
                      <div className={classes.dropdownContainer}>
                        <Dropdown
                          isSearchEnabled={true}
                          disabled={isEditDisabled || isViewDisable}
                          getPopupContainer={() => document.body}
                          overlay={
                            <DropdownMenu
                              dsVersion="2.0"
                              options={this.getSubjectAndSubjectGroupOptions()}
                              isSearchEnabled={true}
                              placeholder={t("common:search")}
                              onClick={value =>
                                this.handleSubjectGroupSelect(value, index)
                              }
                            />
                          }
                        >
                          <Button
                            dsVersion="2.0"
                            variant="neutral"
                            type="plain"
                            className={classes.subjectButton}
                            size="small"
                          >
                            <div className={subjectLabel}>
                              {!_.isNull(
                                subjectAndSubjectGroupLabel.subject
                              ) ? (
                                <div>
                                  {`${subjectAndSubjectGroupLabel.subject}`}
                                </div>
                              ) : (
                                <span className={classes.placeholder}>
                                  {t("common:select")}
                                </span>
                              )}
                            </div>
                          </Button>
                        </Dropdown>
                      </div>
                      {!isEditDisabled && canBeDeleted && (
                        <div className={classes.removeContainer}>
                          <IconButton
                            dsVersion="2.0"
                            icon={<DeleteOutlined />}
                            variant="neutral"
                            type="plain"
                            onClick={() => this.onDeleteClick({ index })}
                            shouldStopPropagation={true}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isObjectiveLevelsHidden ? (
                  <div className={childPairClasses}>
                    <div
                      className={classNames(
                        "text-label-l",
                        classes.objectLevelIconLabelContainer,
                        {
                          [classes.objectLevelIconLabelContainerCenterAligned]: !isLearningCourseEnabled,
                        }
                      )}
                      ref={this.createObjectiveTitleLabelContainerRefCallback(
                        index
                      )}
                    >
                      {isLearningCourseEnabled ? (
                        <OpenBookOutlined dsVersion="2.0" size="xx-small" />
                      ) : (
                        <GoalsOutlined size="xx-small" />
                      )}
                      {isLearningCourseEnabled ? (
                        <span className={classes.objectLevelLabel}>
                          {t("unitPlan:myp_subject_objective_year_phase", {
                            subject: subjectAndSubjectGroupLabel.subject,
                          })}
                        </span>
                      ) : (
                        t("unitPlan:myp_objective_year_phase")
                      )}
                    </div>
                    <div className={classes.objectLevelContainer}>
                      {isObjectivePhasesEnabled ||
                      isObjectivePhaseRecommendationEnabled ? (
                        <ChecklistDropdown
                          options={this.getFormattedObjectiveLevelOptions(
                            objectiveLevelOptions
                          )}
                          value={
                            _.isArray(objectiveLevelId)
                              ? objectiveLevelId
                              : objectiveLevelId
                              ? [objectiveLevelId]
                              : []
                          }
                          onChange={selectedValues =>
                            this.updateObjectiveOption({
                              key: "objectiveLevelId",
                              value: selectedValues,
                              index,
                            })
                          }
                          placeholder={t("unitPlan:select_year_phase")}
                          showAllOption={false}
                          isConfirmation={false}
                          dsVersion="2.0"
                          testId={`objective-checklist-${index}`}
                          showSelectedOptionsOnTop={false}
                          className={classes.objectiveDropdown}
                          disabled={
                            isObjectiveLevelOptionDisabled ||
                            _.isEmpty(objectiveLevelOptions) ||
                            isViewDisable
                          }
                        >
                          <Button
                            dsVersion="2.0"
                            variant="neutral"
                            type="plain"
                            size="small"
                            className={classes.objectiveButton}
                            disabled={
                              isObjectiveLevelOptionDisabled ||
                              _.isEmpty(objectiveLevelOptions) ||
                              isViewDisable
                            }
                            rightIcon={
                              recommendedObjectiveLevelIsUnSelected ? (
                                <Tooltip
                                  dsVersion="2.0"
                                  tooltip={
                                    <Trans i18nKey="unitPlan:myp_recommended_objective_unit_plan_create_warning_msg">
                                      IB recommends
                                      <strong>
                                        {{
                                          phase:
                                            recommendedObjectiveLevel?.name,
                                        }}
                                      </strong>
                                      {{
                                        grade:
                                          options?.courseDetails?.primaryGrade
                                            ?.name,
                                      }}
                                      for students. Please ensure you are aware
                                      of the selection made before you proceed.
                                    </Trans>
                                  }
                                  placement={"right"}
                                  showArrow={true}
                                >
                                  <WarningCircleFilled variant="warning" />
                                </Tooltip>
                              ) : isObjectivePhasesEnabled &&
                                !isObjectivePhaseRecommendationEnabled &&
                                !_.isEmpty(objectiveLevelId) ? (
                                <Tooltip
                                  dsVersion="2.0"
                                  placement={"right"}
                                  showArrow={true}
                                  tooltip={t(
                                    "unitPlan:myp_objective_unit_plan_create_warning_msg",
                                    {
                                      label: objectiveLevelLabel,
                                    }
                                  )}
                                >
                                  <WarningCircleFilled variant="warning" />
                                </Tooltip>
                              ) : null
                            }
                          >
                            <div
                              className={classNames(
                                classes.objectiveLabels,
                                "text-body",
                                {
                                  [classes.emptyText]: _.isEqual(
                                    objectiveLabels,
                                    t("unitPlan:select_year_phase")
                                  ),
                                }
                              )}
                            >
                              {objectiveLabels}
                            </div>
                          </Button>
                        </ChecklistDropdown>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {multi &&
          mode === "edit" &&
          isInterDisciplinaryUnitPlan &&
          !isLearningCourseEnabled && (
            <div
              className={classNames("text-label-l", classes.addButtonContainer)}
              onClick={this.onAddClick}
            >
              <AddCircleOutlined variant="link" size="xx-small" />
              {t("unitPlan:add_idu_subject_label")}
            </div>
          )}
        {showObjectiveRemoveModal && (
          <Modal
            width="402px"
            height="fit-content"
            onClose={() =>
              this.setState({
                showObjectiveRemoveModal: false,
                objectiveUpdateValue: {},
                removedObjectiveLevel: {},
              })
            }
            dsVersion="2.0"
          >
            <Modal.Header
              isClosable
              title={t("unitPlan:remove_phase_from_unit")}
              dsVersion="2.0"
              hasSeparator
            ></Modal.Header>
            <Modal.Body isScrollable={false} dsVersion="2.0">
              <div className={classes.modalBody}>
                <div className={classes.modalSubTitle}>
                  <Trans
                    i18nKey={"unitPlan:objective_level_change_warning"}
                    label={removedObjectiveLevel.label}
                  >
                    You are about to remove
                    <strong>{{ label: removedObjectiveLevel.label }}</strong>
                    from the unit
                  </Trans>
                </div>
                <Alert
                  type={"warning"}
                  variant="inline"
                  showIcon={false}
                  dsVersion="2.0"
                  message={
                    <ul className={classes.listContainer}>
                      <li>{t("unitPlan:remove_phase_modal_warning_1")}</li>
                      <li>{t("unitPlan:remove_phase_modal_warning_2")}</li>
                    </ul>
                  }
                />
                <div>{t("unitPlan:change_grade_modal_confirmation_msg")}</div>
              </div>
            </Modal.Body>
            <Modal.Footer dsVersion="2.0" hasSeparator>
              <div className={classes.footer}>
                <Button
                  dsVersion="2.0"
                  onClick={() =>
                    this.setState({
                      showObjectiveRemoveModal: false,
                      objectiveUpdateValue: {},
                      removedObjectiveLevel: {},
                    })
                  }
                  variant="neutral"
                  type="outlined"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  dsVersion="2.0"
                  type="fill"
                  onClick={() => {
                    this.updateOption(objectiveUpdateValue);
                    this.setState({
                      showObjectiveRemoveModal: false,
                      objectiveUpdateValue: {},
                      removedObjectiveLevel: {},
                    });
                  }}
                  variant="progressive"
                >
                  {t("common:proceed")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    );
  };
  renderViewSubjectGroupSelect = () => {
    const { subjectGroupPair } = this.state;
    const {
      t,
      options: {
        objectiveLevelNodes,
        selectedObjectiveLevelsWithValue,
        unitType,
        courseDetails,
        subjectGroups: SubjectGroupList,
      } = {},
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
      isLearningCourseEnabled,
    } = this.props;

    const isInterDisciplinaryUnitPlan = unitType == MYP_INTERDISCIPLINARY;

    return (
      <div className={classes.container}>
        <div className={classes.subjectGroupPairs}>
          {_.map(subjectGroupPair, (pair, index) => {
            const { subjectGroup, subject, objectiveLevelId } = pair;

            const objectiveLevelOptions = this.getObjectiveLevelOptions({
              objectiveLevelNodes,
              subject,
              SubjectGroupList,
              courseDetails,
              subjectGroup,
              selectedObjectiveLevelsWithValue,
            });

            const subjectLabel = classNames({
              [classes.subjectLabelViewMode]: true,
              "text-body": true,
            });

            const subjectAndSubjectGroupLabel = this.getSubjectGroupOptionsLabels(
              { subjectId: subject, subjectGroupId: subjectGroup }
            );
            const titleContainerClass = classNames({
              [classes.titleContainer]: isInterDisciplinaryUnitPlan,
              [classes.SubjectSpecificTitleContainer]: true,
            });

            const selectedValue = this.getSelectValue({
              pair,
              objectiveLevelOptions,
            });

            let objectiveLabels;
            if (!_.isEmpty(objectiveLevelOptions)) {
              if (_.isArray(objectiveLevelId)) {
                if (_.isArray(selectedValue) && selectedValue.length > 0) {
                  objectiveLabels = _.join(_.map(selectedValue, "name"), ", ");
                } else {
                  objectiveLabels = t("common:not_selected");
                }
              } else if (selectedValue) {
                objectiveLabels = _.get(selectedValue, "name", "");
              } else {
                objectiveLabels = t("common:not_selected");
              }
            } else {
              objectiveLabels = t("common:not_selected");
            }

            const childPairClasses = classNames({
              [classes.childPair]: true,
              [classes.childPairV2]: isLearningCourseEnabled,
            });

            return (
              <div className={classes.pairContainer} key={index}>
                {!isLearningCourseEnabled && (
                  <div className={classes.pair}>
                    <div
                      className={titleContainerClass}
                      ref={this.createSubjectTitleLabelContainerRefCallback(
                        index
                      )}
                    >
                      <OpenBookTextOutlined size="xx-small" />
                      <div className={classNames("text-label-l")}>
                        {isInterDisciplinaryUnitPlan
                          ? t("unitPlan:unitType_subject_count", {
                              unitType: t("common:interdisciplinary"),
                              count: index + 1,
                            })
                          : t("common:subject")}
                      </div>
                    </div>
                    <div className={subjectLabel}>
                      {!_.isNull(subjectAndSubjectGroupLabel.subject) ? (
                        <div>{`${subjectAndSubjectGroupLabel.subject}`}</div>
                      ) : (
                        <span className={classes.placeholder}>
                          {t("common:not_selected")}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className={childPairClasses}>
                  <div
                    className={classNames(
                      "text-label-l",
                      classes.objectLevelIconLabelContainer,
                      {
                        [classes.objectLevelIconLabelContainerCenterAligned]: !isLearningCourseEnabled,
                      }
                    )}
                    ref={this.createObjectiveTitleLabelContainerRefCallback(
                      index
                    )}
                  >
                    {isLearningCourseEnabled ? (
                      <OpenBookOutlined dsVersion="2.0" size="xx-small" />
                    ) : (
                      <GoalsOutlined size="xx-small" />
                    )}
                    {isLearningCourseEnabled ? (
                      <span className={classes.objectLevelLabel}>
                        {t("unitPlan:myp_subject_objective_year_phase", {
                          subject: subjectAndSubjectGroupLabel.subject,
                        })}
                      </span>
                    ) : (
                      t("unitPlan:myp_objective_year_phase")
                    )}
                  </div>
                  <div
                    className={classNames(classes.objectLevelContainer, {
                      [classes.objectLevelContainerV2]: true,
                    })}
                  >
                    {isObjectivePhasesEnabled ||
                    isObjectivePhaseRecommendationEnabled ? (
                      <div
                        className={classNames(
                          "text-body",
                          classes.objectiveLabelsViewMode,
                          {
                            [classes.emptyText]: _.isEqual(
                              objectiveLabels,
                              t("common:not_selected")
                            ),
                          }
                        )}
                      >
                        {objectiveLabels}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  renderView = () => this.renderViewSubjectGroupSelect();
  renderEdit = () => this.renderEditSubjectGroupSelect();
  shouldShowViewEmpty = () => {
    const { subjectGroupPair } = this.state;
    return _.isEmpty(subjectGroupPair);
  };
  renderViewEmpty = () => {
    const {
      t,
      options: { unitType } = {},
      isObjectivePhasesEnabled,
      isObjectivePhaseRecommendationEnabled,
    } = this.props;
    const isInterDisciplinaryUnitPlan = unitType == MYP_INTERDISCIPLINARY;
    const index = 0;
    <div className={classes.pairContainer}>
      <div className={classes.pair}>
        <div ref={this.createSubjectTitleLabelContainerRefCallback(index)}>
          <OpenBookTextOutlined size="xx-small" />
          <div className={classNames("text-label-l")}>
            {isInterDisciplinaryUnitPlan
              ? t("unitPlan:unitType_subject_count", {
                  unitType: t("common:interdisciplinary"),
                  count: index + 1,
                })
              : t("common:subject")}
          </div>
        </div>
        <div>
          <span className={classes.placeholder}>
            {t("common:not_selected")}
          </span>
        </div>
      </div>
      <div className={classes.childPair}>
        <div
          className={classNames(
            "text-label-l",
            classes.objectLevelIconLabelContainer
          )}
          ref={this.createObjectiveTitleLabelContainerRefCallback(index)}
        >
          <GoalsOutlined size="xx-small" />
          {t("unitPlan:myp_objective_year_phase")}
        </div>
        <div
          className={classNames(classes.objectLevelContainer, {
            [classes.objectLevelContainerV2]: true,
          })}
        >
          {isObjectivePhasesEnabled || isObjectivePhaseRecommendationEnabled ? (
            <div className={classNames("text-body", classes.emptyText)}>
              {t("common:not_selected")}
            </div>
          ) : null}
        </div>
      </div>
    </div>;
  };
  getSelectValue = ({ pair, objectiveLevelOptions }) => {
    const { objectiveLevelId: value } = pair;
    let options = [...objectiveLevelOptions];
    const isGroupedOptions = !_.isEmpty(_.get(_.head(options), "options", []));
    if (isGroupedOptions) options = _.flatMap(options, "options");

    if (_.isArray(value)) {
      const valuesObj = _.map(value, value =>
        _.find(options, option => option.value === value)
      ).filter(Boolean);
      return valuesObj;
    } else {
      const groupedDropdown = _.some(
        options,
        option => !_.isEmpty(option?.options)
      );
      if (groupedDropdown) {
        const groupedOptions = _.reduce(
          options,
          (result = [], option) => {
            result = _.concat(result, _.get(option, "options", []));

            return result;
          },
          []
        );
        const valueObj = _.find(
          groupedOptions,
          option => option.value === value
        );
        return valueObj ?? null;
      } else {
        const valueObj = _.find(options, option => option.value === value);
        return valueObj ?? null;
      }
    }
  };
}

const mapStateToProps = () => {
  const isObjectivePhasesEnabled = !ACLStore.can(
    "FeatureFlag:RemoveMYPObjectivesPhase"
  );
  const isObjectivePhaseRecommendationEnabled = ACLStore.can(
    ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION
  );
  const isLearningCourseEnabled = getIsLearningCoursesEnabled();
  return {
    isObjectivePhasesEnabled,
    isObjectivePhaseRecommendationEnabled,
    isLearningCourseEnabled,
  };
};

SubjectGroupSelect.propTypes = {
  ...UIBaseComponent.propTypes,
};

SubjectGroupSelect.defaultProps = {
  ...UIBaseComponent.defaultProps,
  multi: true,
  isAbsoluteCancelIcon: true,
  isObjectiveLevelsHidden: false,
  registerLabelContainerRef: _.noop,
  subjectTitleStyles: {},
};

export default compose(
  connect(mapStateToProps, null, null, { withRef: true }),
  I18nHOC({ resource: ["common", "unitPlan"] })
)(SubjectGroupSelect);
